import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Building2,
  TrendingUp,
  TrendingDown,
  DollarSign,
  RefreshCw,
  Search,
  ArrowUpDown,
  Eye,
} from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { api } from '../../lib/api';
import { ENDPOINTS } from '../../config/api';
import { formatCurrency } from '../../lib/utils';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

interface TenantRevenue {
  tenantId: string;
  tenantName: string;
  totalOperations: number;
  totalGrossAmount: number;
  totalPlatformFees: number;
  totalPlatformMargin: number;
  totalTenantFees: number;
  totalTenantMargin: number;
  avgTransactionValue: number;
  growthRate?: number;
  byOperationType?: Array<{
    operationType: string;
    count: number;
    grossAmount: number;
    platformMargin: number;
  }>;
}

interface TenantDetail {
  tenant: TenantRevenue;
  dailyTrend: Array<{
    date: string;
    operations: number;
    grossAmount: number;
    platformMargin: number;
  }>;
}

const COLORS = ['#06b6d4', '#8b5cf6', '#f59e0b', '#10b981', '#ef4444', '#3b82f6'];

const OPERATION_LABELS: Record<string, string> = {
  PIX_RECEIVE: 'Pix Recebido',
  PIX_SEND: 'Pix Enviado',
  TRANSFER: 'Transferência',
  BOLETO: 'Boleto',
  CARD: 'Cartão',
  WITHDRAWAL: 'Saque',
};

export function TenantRevenueList() {
  const [period, setPeriod] = useState('30d');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'margin' | 'volume' | 'operations'>('margin');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedTenant, setSelectedTenant] = useState<TenantRevenue | null>(null);

  const { startDate, endDate } = useMemo(() => {
    const now = new Date();
    let start: Date;
    
    switch (period) {
      case '7d':
        start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case '30d':
        start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case '90d':
        start = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
        break;
      default:
        start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    }

    return {
      startDate: start.toISOString().split('T')[0],
      endDate: now.toISOString().split('T')[0],
    };
  }, [period]);

  // Fetch tenant revenues
  const { data: tenants, isLoading, refetch } = useQuery<TenantRevenue[]>({
    queryKey: ['tenant-revenues', startDate, endDate],
    queryFn: async () => {
      const res = await api.get(ENDPOINTS.revenue.platformSummary, {
        params: { startDate, endDate, groupBy: 'tenant' },
      });
      return res.data.byTenant || [];
    },
  });

  // Fetch tenant detail
  const { data: tenantDetail } = useQuery<TenantDetail>({
    queryKey: ['tenant-revenue-detail', selectedTenant?.tenantId, startDate, endDate],
    queryFn: async () => {
      if (!selectedTenant) return null;
      const res = await api.get(`/revenue/tenant/${selectedTenant.tenantId}`, {
        params: { startDate, endDate },
      });
      return res.data;
    },
    enabled: !!selectedTenant,
  });

  // Filter and sort tenants
  const filteredTenants = useMemo(() => {
    let result = tenants || [];
    
    // Filter by search term
    if (searchTerm) {
      result = result.filter((t) =>
        t.tenantName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.tenantId.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Sort
    result = [...result].sort((a, b) => {
      let valueA: number, valueB: number;
      
      switch (sortBy) {
        case 'margin':
          valueA = a.totalPlatformMargin;
          valueB = b.totalPlatformMargin;
          break;
        case 'volume':
          valueA = a.totalGrossAmount;
          valueB = b.totalGrossAmount;
          break;
        case 'operations':
          valueA = a.totalOperations;
          valueB = b.totalOperations;
          break;
        default:
          valueA = a.totalPlatformMargin;
          valueB = b.totalPlatformMargin;
      }
      
      return sortOrder === 'desc' ? valueB - valueA : valueA - valueB;
    });
    
    return result;
  }, [tenants, searchTerm, sortBy, sortOrder]);

  // Calculate totals
  const totals = useMemo(() => {
    if (!tenants) return { volume: 0, margin: 0, operations: 0 };
    return tenants.reduce(
      (acc, t) => ({
        volume: acc.volume + t.totalGrossAmount,
        margin: acc.margin + t.totalPlatformMargin,
        operations: acc.operations + t.totalOperations,
      }),
      { volume: 0, margin: 0, operations: 0 }
    );
  }, [tenants]);

  const toggleSort = (field: 'margin' | 'volume' | 'operations') => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Receita por Tenant</h1>
          <p className="text-surface-400 mt-1">
            Análise detalhada de receita por tenant
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            className="w-32"
          >
            <option value="7d">7 dias</option>
            <option value="30d">30 dias</option>
            <option value="90d">90 dias</option>
          </Select>
          <Button
            variant="ghost"
            onClick={() => refetch()}
            disabled={isLoading}
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card variant="stat">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-surface-400 text-sm">Total Tenants</p>
              <p className="text-2xl font-bold text-white mt-1">
                {filteredTenants.length}
              </p>
            </div>
            <div className="p-2 bg-primary-500/20 rounded-lg">
              <Building2 className="w-5 h-5 text-primary-400" />
            </div>
          </div>
        </Card>

        <Card variant="stat">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-surface-400 text-sm">Volume Total</p>
              <p className="text-2xl font-bold text-white mt-1">
                {formatCurrency(totals.volume)}
              </p>
              <p className="text-surface-500 text-xs mt-1">
                {totals.operations.toLocaleString()} operações
              </p>
            </div>
            <div className="p-2 bg-accent-500/20 rounded-lg">
              <DollarSign className="w-5 h-5 text-accent-400" />
            </div>
          </div>
        </Card>

        <Card variant="stat">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-surface-400 text-sm">Margem Total</p>
              <p className="text-2xl font-bold text-green-400 mt-1">
                {formatCurrency(totals.margin)}
              </p>
            </div>
            <div className="p-2 bg-green-500/20 rounded-lg">
              <TrendingUp className="w-5 h-5 text-green-400" />
            </div>
          </div>
        </Card>
      </div>

      {/* Search and Filters */}
      <Card>
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-400" />
            <Input
              placeholder="Buscar tenant..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'margin' | 'volume' | 'operations')}
            className="w-40"
          >
            <option value="margin">Ordenar por Margem</option>
            <option value="volume">Ordenar por Volume</option>
            <option value="operations">Ordenar por Operações</option>
          </Select>
        </div>
      </Card>

      {/* Table */}
      <Card className="overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 text-primary-500 animate-spin" />
          </div>
        ) : !filteredTenants || filteredTenants.length === 0 ? (
          <div className="text-center py-12">
            <Building2 className="w-12 h-12 text-surface-600 mx-auto mb-4" />
            <p className="text-surface-400">Nenhum tenant encontrado</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-surface-800">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-surface-400">Tenant</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-surface-400">
                  <button
                    className="flex items-center gap-1 hover:text-primary-400"
                    onClick={() => toggleSort('operations')}
                  >
                    Operações
                    <ArrowUpDown className="w-3 h-3" />
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-surface-400">
                  <button
                    className="flex items-center gap-1 hover:text-primary-400"
                    onClick={() => toggleSort('volume')}
                  >
                    Volume
                    <ArrowUpDown className="w-3 h-3" />
                  </button>
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-surface-400">
                  <button
                    className="flex items-center gap-1 hover:text-primary-400"
                    onClick={() => toggleSort('margin')}
                  >
                    Margem
                    <ArrowUpDown className="w-3 h-3" />
                  </button>
                </th>
                <th className="px-4 py-3 text-right text-sm font-medium text-surface-400">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-700">
              {filteredTenants.map((tenant) => (
                <tr key={tenant.tenantId} className="hover:bg-surface-800/50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-primary-500/20 flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-primary-400" />
                      </div>
                      <div>
                        <p className="font-medium text-white">{tenant.tenantName || 'Sem nome'}</p>
                        <p className="text-xs text-surface-500">{tenant.tenantId.slice(0, 12)}...</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-center">
                      <p className="font-medium text-white">{tenant.totalOperations.toLocaleString()}</p>
                      <p className="text-xs text-surface-500">
                        Média: {formatCurrency(tenant.avgTransactionValue || 0)}
                      </p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-medium text-white">{formatCurrency(tenant.totalGrossAmount)}</p>
                      <p className="text-xs text-surface-500">
                        Taxas: {formatCurrency(tenant.totalPlatformFees)}
                      </p>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-green-400">
                        {formatCurrency(tenant.totalPlatformMargin)}
                      </span>
                      {tenant.growthRate !== undefined && (
                        <Badge
                          variant={tenant.growthRate >= 0 ? 'success' : 'error'}
                          className="text-xs"
                        >
                          {tenant.growthRate >= 0 ? (
                            <TrendingUp className="w-3 h-3 mr-1" />
                          ) : (
                            <TrendingDown className="w-3 h-3 mr-1" />
                          )}
                          {Math.abs(tenant.growthRate).toFixed(1)}%
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedTenant(tenant)}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {/* Tenant Detail Modal */}
      <Modal
        isOpen={!!selectedTenant}
        onClose={() => setSelectedTenant(null)}
        title={`Detalhes - ${selectedTenant?.tenantName || 'Tenant'}`}
        size="lg"
      >
        {selectedTenant && (
          <div className="space-y-6">
            {/* Summary */}
            <div className="grid grid-cols-3 gap-4">
              <div className="p-4 bg-surface-800/50 rounded-lg">
                <p className="text-surface-400 text-sm">Volume</p>
                <p className="text-xl font-bold text-white mt-1">
                  {formatCurrency(selectedTenant.totalGrossAmount)}
                </p>
              </div>
              <div className="p-4 bg-surface-800/50 rounded-lg">
                <p className="text-surface-400 text-sm">Operações</p>
                <p className="text-xl font-bold text-white mt-1">
                  {selectedTenant.totalOperations.toLocaleString()}
                </p>
              </div>
              <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                <p className="text-green-400 text-sm">Margem</p>
                <p className="text-xl font-bold text-green-400 mt-1">
                  {formatCurrency(selectedTenant.totalPlatformMargin)}
                </p>
              </div>
            </div>

            {/* Charts */}
            <div className="grid grid-cols-2 gap-6">
              {/* Daily Trend */}
              <div>
                <h4 className="text-sm font-medium text-surface-300 mb-3">
                  Evolução Diária
                </h4>
                <div className="h-48">
                  {tenantDetail?.dailyTrend && tenantDetail.dailyTrend.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={tenantDetail.dailyTrend}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis
                          dataKey="date"
                          stroke="#9ca3af"
                          fontSize={10}
                          tickFormatter={(value) =>
                            new Date(value).toLocaleDateString('pt-BR', {
                              day: '2-digit',
                              month: '2-digit',
                            })
                          }
                        />
                        <YAxis
                          stroke="#9ca3af"
                          fontSize={10}
                          tickFormatter={(value) => `R$ ${(value / 100).toFixed(0)}`}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#1f2937',
                            border: '1px solid #374151',
                            borderRadius: '8px',
                          }}
                          formatter={(value) => [formatCurrency(Number(value) || 0), 'Margem']}
                        />
                        <Bar dataKey="platformMargin" fill="#06b6d4" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-surface-500">
                      Sem dados
                    </div>
                  )}
                </div>
              </div>

              {/* By Operation Type */}
              <div>
                <h4 className="text-sm font-medium text-surface-300 mb-3">
                  Por Tipo de Operação
                </h4>
                <div className="h-48">
                  {selectedTenant.byOperationType && selectedTenant.byOperationType.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={selectedTenant.byOperationType.map((item) => ({
                            name: OPERATION_LABELS[item.operationType] || item.operationType,
                            value: item.platformMargin,
                          }))}
                          cx="50%"
                          cy="50%"
                          innerRadius={40}
                          outerRadius={60}
                          paddingAngle={5}
                          dataKey="value"
                        >
                          {selectedTenant.byOperationType.map((_, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#1f2937',
                            border: '1px solid #374151',
                            borderRadius: '8px',
                          }}
                          formatter={(value) => [formatCurrency(Number(value) || 0), 'Margem']}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-surface-500">
                      Sem dados
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Operation Breakdown */}
            {selectedTenant.byOperationType && selectedTenant.byOperationType.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-surface-300 mb-3">
                  Breakdown por Operação
                </h4>
                <div className="space-y-2">
                  {selectedTenant.byOperationType.map((op, index) => (
                    <div
                      key={op.operationType}
                      className="flex items-center justify-between p-3 bg-surface-800/50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        />
                        <span className="text-surface-300">
                          {OPERATION_LABELS[op.operationType] || op.operationType}
                        </span>
                      </div>
                      <div className="flex items-center gap-6 text-sm">
                        <span className="text-surface-400">{op.count} ops</span>
                        <span className="text-surface-300">
                          {formatCurrency(op.grossAmount)}
                        </span>
                        <span className="text-green-400 font-medium">
                          {formatCurrency(op.platformMargin)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}

export default TenantRevenueList;
